/**
 * Design System Showcase
 * Demonstrates all Option B aesthetic elements for developers
 */

import { useState } from 'react';
import { 
  Copy, 
  Check, 
  Palette, 
  Type, 
  Box, 
  Cloud,
  Layers
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  colors,
  typography,
  shadows,
  radius,
  components,
  grainTexture,
  generateCSSVariables,
} from '@/styles/design-system';

export function DesignSystemShowcase() {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const handleCopy = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const colorGroups = [
    { name: 'Primary (Deep Navy)', colors: colors.primary, base: colors.primary.DEFAULT },
    { name: 'Accent (Marple Orange)', colors: colors.accent, base: colors.accent.DEFAULT },
    { name: 'Brand (Marple Green)', colors: colors.brand, base: colors.brand.DEFAULT },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <header className="bg-[#061B37] text-white py-16">
        <div className="container-marple">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 bg-[#F7971C]/20 text-[#F7971C] rounded-full text-sm font-medium mb-4">
              For Developers
            </span>
            <h1 className="heading-display text-white mb-4">
              Option B Design System
            </h1>
            <p className="text-xl text-white/80">
              Comprehensive styling rules for consistent Marple & Co UI development.
              Maintain the heritage aesthetic with modern implementation.
            </p>
          </div>
        </div>
      </header>

      <div className="container-marple py-12 space-y-16">
        {/* Color Palette */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-6 h-6 text-[#061B37]" />
            <h2 className="heading-2">Color Palette</h2>
          </div>
          
          <div className="grid gap-8">
            {colorGroups.map((group) => (
              <div key={group.name} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#061B37]">{group.name}</h3>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{group.base}</code>
                </div>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {Object.entries(group.colors).map(([key, value]) => (
                    <div key={key} className="group relative">
                      <div 
                        className="aspect-square rounded-lg cursor-pointer transition-transform hover:scale-110"
                        style={{ backgroundColor: value }}
                        onClick={() => handleCopy(value, `${group.name}-${key}`)}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {copiedSection === `${group.name}-${key}` ? (
                          <Check className="w-4 h-4 text-white drop-shadow-md" />
                        ) : (
                          <Copy className="w-4 h-4 text-white drop-shadow-md" />
                        )}
                      </div>
                      <p className="text-xs text-center mt-1 text-gray-500">{key}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Type className="w-6 h-6 text-[#061B37]" />
            <h2 className="heading-2">Typography</h2>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 space-y-8">
            {/* Font Families */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Font Families
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Headings</p>
                  <p className="font-heading text-2xl text-[#061B37]">
                    Novecento Sans
                  </p>
                  <code className="text-xs text-gray-400">font-family: {typography.fonts.heading}</code>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Body</p>
                  <p className="font-body text-lg text-[#061B37]">
                    Avenir Next - Clean and readable body text
                  </p>
                  <code className="text-xs text-gray-400">font-family: {typography.fonts.body}</code>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Quotes</p>
                  <p className="font-quote text-lg text-[#061B37]">
                    "Garamond for elegant quotes and emphasis"
                  </p>
                  <code className="text-xs text-gray-400">font-family: {typography.fonts.quote}</code>
                </div>
              </div>
            </div>

            {/* Heading Styles */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Heading Styles
              </h3>
              <div className="space-y-4">
                <div className="flex items-baseline gap-4">
                  <span className="text-sm text-gray-400 w-24">Display</span>
                  <h1 className="heading-display">Heading Display</h1>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-sm text-gray-400 w-24">H1</span>
                  <h1 className="heading-1">Heading 1</h1>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-sm text-gray-400 w-24">H2</span>
                  <h2 className="heading-2">Heading 2</h2>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-sm text-gray-400 w-24">H3</span>
                  <h3 className="heading-3">Heading 3</h3>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Shadows */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Cloud className="w-6 h-6 text-[#061B37]" />
            <h2 className="heading-2">Shadows</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(shadows).map(([name, value]) => (
              <div 
                key={name}
                className="bg-white rounded-xl p-6 h-32 flex items-center justify-center"
                style={{ boxShadow: value }}
              >
                <div className="text-center">
                  <p className="font-semibold text-[#061B37] capitalize mb-1">{name}</p>
                  <code className="text-xs text-gray-400">{value.slice(0, 40)}...</code>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Components */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Box className="w-6 h-6 text-[#061B37]" />
            <h2 className="heading-2">Components</h2>
          </div>
          
          <div className="space-y-8">
            {/* Buttons */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h3 className="heading-3 mb-6">Buttons</h3>
              <div className="flex flex-wrap gap-4">
                <button className="btn btn-primary">Primary Button</button>
                <button className="btn btn-accent">Accent Button</button>
                <button className="btn btn-brand">Brand Button</button>
                <button className="btn btn-outline">Outline Button</button>
                <button className="btn btn-ghost">Ghost Button</button>
              </div>
              <div className="flex flex-wrap gap-4 mt-4">
                <button className="btn btn-primary btn-sm">Small</button>
                <button className="btn btn-primary">Default</button>
                <button className="btn btn-primary btn-lg">Large</button>
              </div>
            </div>

            {/* Cards */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h3 className="heading-3 mb-6">Cards</h3>
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="card p-6">
                  <p className="font-semibold text-[#061B37] mb-2">Default Card</p>
                  <p className="text-sm text-gray-600">Standard card with soft shadow</p>
                </div>
                <div className="card card-hover p-6 cursor-pointer">
                  <p className="font-semibold text-[#061B37] mb-2">Hover Card</p>
                  <p className="text-sm text-gray-600">Hover to see elevation effect</p>
                </div>
                <div className="card card-bordered p-6">
                  <p className="font-semibold text-[#061B37] mb-2">Bordered Card</p>
                  <p className="text-sm text-gray-600">Subtle border instead of shadow</p>
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h3 className="heading-3 mb-6">Badges</h3>
              <div className="flex flex-wrap gap-3">
                <span className="badge badge-primary">Primary</span>
                <span className="badge badge-accent">Accent</span>
                <span className="badge badge-brand">Brand</span>
                <span className="badge badge-neutral">Neutral</span>
              </div>
            </div>

            {/* Form Inputs */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h3 className="heading-3 mb-6">Form Inputs</h3>
              <div className="max-w-md space-y-4">
                <input 
                  type="text" 
                  placeholder="Enter your name..."
                  className="input"
                />
                <input 
                  type="email" 
                  placeholder="Enter your email..."
                  className="input"
                />
                <textarea 
                  placeholder="Enter your message..."
                  rows={3}
                  className="input resize-none"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Grain Texture */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Layers className="w-6 h-6 text-[#061B37]" />
            <h2 className="heading-2">Grain Texture</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500 mb-4">Without Texture</p>
              <div className="h-32 bg-gradient-to-br from-[#061B37] to-[#0D2A52] rounded-lg" />
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500 mb-4">With Grain Texture (2% opacity)</p>
              <div className="grain-texture h-32 bg-gradient-to-br from-[#061B37] to-[#0D2A52] rounded-lg flex items-center justify-center">
                <span className="text-white/60 text-sm">Subtle texture overlay</span>
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Usage:</p>
            <code className="text-xs bg-white px-2 py-1 rounded border">
              &lt;div className="grain-texture"&gt;...&lt;/div&gt;
            </code>
          </div>
        </section>

        {/* CSS Variables */}
        <section>
          <h2 className="heading-2 mb-6">CSS Variables</h2>
          <div className="bg-[#061B37] rounded-xl p-6 overflow-x-auto">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/60 text-sm">Copy to your CSS file</span>
              <button 
                onClick={() => handleCopy(generateCSSVariables(), 'css-variables')}
                className="flex items-center gap-2 text-white/80 hover:text-white text-sm"
              >
                {copiedSection === 'css-variables' ? (
                  <><Check className="w-4 h-4" /> Copied!</>
                ) : (
                  <><Copy className="w-4 h-4" /> Copy All</>
                )}
              </button>
            </div>
            <pre className="text-sm text-green-400 font-mono whitespace-pre">
              {generateCSSVariables()}
            </pre>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
          <h2 className="heading-2 mb-6">Quick Reference</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-[#061B37] mb-2">Primary Color</h4>
              <code className="bg-gray-100 px-2 py-1 rounded">#061B37</code>
              <p className="text-gray-500 mt-1">Deep Navy - Use for headings, primary buttons</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#061B37] mb-2">Accent Color</h4>
              <code className="bg-gray-100 px-2 py-1 rounded">#F7971C</code>
              <p className="text-gray-500 mt-1">Marple Orange - CTAs, highlights</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#061B37] mb-2">Brand Color</h4>
              <code className="bg-gray-100 px-2 py-1 rounded">#9FC131</code>
              <p className="text-gray-500 mt-1">Marple Green - Success states, badges</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#061B37] mb-2">Card Radius</h4>
              <code className="bg-gray-100 px-2 py-1 rounded">12px</code>
              <p className="text-gray-500 mt-1">Use for cards, modals, containers</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#061B37] mb-2">Button Radius</h4>
              <code className="bg-gray-100 px-2 py-1 rounded">9999px (pill)</code>
              <p className="text-gray-500 mt-1">All buttons should be pill-shaped</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#061B37] mb-2">Shadow</h4>
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">0 10px 30px rgba(6,27,55,0.08)</code>
              <p className="text-gray-500 mt-1">Soft, organic shadows</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default DesignSystemShowcase;
